import { Group } from '@visx/group';
import { Pie as VisxPie } from '@visx/shape';

import { Bit } from '@blockworks/ui/bit';
import { useMergeRefs } from '@blockworks/ui/hooks';
import { Text } from '@blockworks/ui/text';

import { useVisualizationOptionsContext } from '../../../../../context';
import { formatValue } from '../../../../format/utils';
import { useResponsiveChart, useVisualizationTooltip } from '../../../utils';
import { TooltipContainer } from '../tooltip';

import { PieArcs } from './arcs';
import { PieLegend } from './legend';
import { PieProps, PieTooltipData, TOOLTIP_STYLES } from './pie.model';
import { usePieOptions } from './utils';

const Pie = ({ activeControls, options, params, data: rawData }: PieProps) => {
    const { layout } = useVisualizationOptionsContext();
    const canShowLegend = !layout?.hideLegend;
    const { containerRef: responsiveRef, ...dimensions } = useResponsiveChart(
        canShowLegend ? options.legend.position : 'none',
    );
    const {
        data,
        partitions,
        container,
        radius,
        legendPosition,
        aggregrate,
        showPartitionLabels,
        orientation,
        translate,
    } = usePieOptions({
        options,
        activeControls,
        params,
        data: rawData,
        ...dimensions,
    });

    const { tooltipData, tooltipLeft, tooltipTop, hideTooltip, containerRef, showTooltip, TooltipInPortal } =
        useVisualizationTooltip<PieTooltipData>();

    const mergeRef = useMergeRefs(responsiveRef, containerRef);

    if (!partitions) return null;

    return (
        <Bit.div
            ref={mergeRef}
            w="full"
            h="full"
            display="flex"
            pos="relative"
            overflow="visible"
            justifyContent="between"
        >
            <svg
                width={container.width}
                height={container.height}
                style={{ marginLeft: container.marginLeft, marginTop: container.marginTop }}
            >
                <Group {...translate}>
                    <VisxPie
                        data={partitions}
                        pieValue={d => data[d.column]}
                        innerRadius={radius.inner}
                        outerRadius={radius.outer}
                        cornerRadius={1}
                        padAngle={0.02}
                    >
                        {props => (
                            <PieArcs
                                pie={props}
                                partitions={partitions}
                                aggregrate={aggregrate}
                                showTooltip={showTooltip}
                                hideTooltip={hideTooltip}
                                tooltipData={tooltipData}
                                showPartitionLabels={showPartitionLabels}
                                isDonut={options.isDonut}
                                radius={radius}
                            />
                        )}
                    </VisxPie>
                </Group>
            </svg>

            {tooltipData && (
                <TooltipInPortal
                    key={Math.random()}
                    applyPositionStyle
                    unstyled
                    left={tooltipLeft}
                    top={tooltipTop}
                    style={TOOLTIP_STYLES}
                >
                    <TooltipContainer>
                        <Text size="xs" weight="semibold">
                            {tooltipData.label}
                        </Text>
                        <Text size="xs">{formatValue(tooltipData.value, tooltipData.format, 'number')}</Text>
                    </TooltipContainer>
                </TooltipInPortal>
            )}
            {canShowLegend && legendPosition && legendPosition !== 'none' && (
                <PieLegend partitions={partitions} position={legendPosition} orientation={orientation} />
            )}
        </Bit.div>
    );
};

export { Pie };
